/**
 * Human Resources · attendance sheet.
 *
 * One day, everybody, one tap per person: present, late, absent, on leave,
 * sick, remote. Leave approved in a staff file is already on the sheet. The
 * people not yet recorded can be marked present in one go, and each name
 * opens the person's file.
 */
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useHrAttendanceDay, useHrSetAttendance } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Button, Card, EmptyState, Field, Input, Segmented, Select, Skeleton, Table } from "../../design/components";
import { ATTENDANCE, ATTENDANCE_BY, type AttendanceStatus } from "../../domain/hrFile";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { CheckIns, Locations, Readers } from "./AttendanceTabs";
import { useHrAccess } from "./access";
import { addDays, Avatar, fileLink, h, niceDate, PreviewBar, todayIso } from "./kit";
import { HrError, s, SampleBanner, SUITE } from "./shared";
import { Link } from "react-router-dom";

type Show = "all" | "present" | "late" | "absent" | "away" | "none";
type View = "sheet" | "checkins" | "locations" | "readers";
const VIEWS: { value: View; label: string }[] = [
  { value: "sheet", label: "Attendance sheet" },
  { value: "checkins", label: "Check-ins" },
  { value: "locations", label: "Locations" },
  { value: "readers", label: "Face & fingerprint readers" },
];

export function HrAttendancePage() {
  const session = useSession();
  const [params, setParams] = useSearchParams();
  const [day, setDay] = useState(params.get("day") ?? todayIso());
  const show = (params.get("show") as Show) || "all";
  const view = (params.get("view") as View) || "sheet";
  const setView = (v: View) => {
    const next = new URLSearchParams(params);
    if (v === "sheet") next.delete("view");
    else next.set("view", v);
    next.delete("show");
    setParams(next, { replace: true });
  };
  const [dept, setDept] = useState("");
  const q = useHrAttendanceDay(day);
  const set = useHrSetAttendance();
  const { can } = useHrAccess();
  const rows = q.data?.rows ?? [];
  const departments = useMemo(() => [...new Set(rows.map((r) => r.department).filter(Boolean))] as string[], [rows]);
  const visible = rows.filter((r) => {
    if (dept && r.department !== dept) return false;
    switch (show) {
      case "present":
        return r.status === "present" || r.status === "remote";
      case "late":
        return r.status === "late";
      case "absent":
        return r.status === "absent";
      case "away":
        return r.status === "leave" || r.status === "sick";
      case "none":
        return !r.status;
      default:
        return true;
    }
  });
  const missing = rows.filter((r) => !r.status);
  const count = (f: (s: AttendanceStatus | null) => boolean) => rows.filter((r) => f(r.status)).length;

  useScreenContext(
    q.data
      ? {
          route: "/hr-attendance",
          title: "Attendance",
          summary: [
            t("{d}: {p} in, {l} late, {a} absent, {v} on leave or sick, {n} not recorded.", {
              d: niceDate(day),
              p: count((x) => x === "present" || x === "remote"),
              l: count((x) => x === "late"),
              a: count((x) => x === "absent"),
              v: count((x) => x === "leave" || x === "sick"),
              n: missing.length,
            }),
          ],
          offers: [{ label: t("Who is absent today?"), ask: "who is absent today?" }],
          worksInFull: true,
        }
      : null,
  );

  const mark = (ids: string[], status: AttendanceStatus) =>
    set.mutate({
      employeeIds: ids,
      day,
      status,
      checkIn: status === "present" && day === todayIso() ? new Date().toTimeString().slice(0, 5) : null,
    });

  const filters: { key: Show; label: string; n: number; c: string }[] = [
    { key: "all", label: "Everyone", n: rows.length, c: "var(--blue)" },
    { key: "present", label: "In", n: count((x) => x === "present" || x === "remote"), c: "var(--green)" },
    { key: "late", label: "Late", n: count((x) => x === "late"), c: "var(--amber)" },
    { key: "absent", label: "Absent", n: count((x) => x === "absent"), c: "var(--red)" },
    { key: "away", label: "On leave or sick", n: count((x) => x === "leave" || x === "sick"), c: "#b07ce8" },
    { key: "none", label: "Not recorded yet", n: missing.length, c: "var(--dim)" },
  ];

  return (
    <Page eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })} title="Attendance">
      <PreviewBar />
      <SampleBanner />
      <Segmented value={view} options={VIEWS} onChange={setView} ariaLabel="Attendance views" />
      {view === "checkins" && (
        <>
          <div className={s.filters}>
            <Button onClick={() => setDay(addDays(day, -1))} aria-label={t("The day before")}>
              ◀
            </Button>
            <Field label="Day">
              <Input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} />
            </Field>
            <Button
              onClick={() => setDay(addDays(day, 1))}
              aria-label={t("The day after")}
              disabled={day >= todayIso()}
            >
              ▶
            </Button>
          </div>
          <CheckIns day={day} />
        </>
      )}
      {view === "locations" && <Locations />}
      {view === "readers" && <Readers />}
      {view === "sheet" && (
        <>
          <Card>
            <div className={s.filters}>
              <Button onClick={() => setDay(addDays(day, -1))} aria-label={t("The day before")}>
                ◀
              </Button>
              <Field label="Day">
                <Input
                  type="date"
                  value={day}
                  onChange={(e) => e.target.value && setDay(e.target.value)}
                  data-testid="hr-att-day"
                />
              </Field>
              <Button
                onClick={() => setDay(addDays(day, 1))}
                aria-label={t("The day after")}
                disabled={day >= todayIso()}
              >
                ▶
              </Button>
              {day !== todayIso() && <Button onClick={() => setDay(todayIso())}>{t("Today")}</Button>}
              <Field label="Department">
                <Select value={dept} onChange={(e) => setDept(e.target.value)}>
                  <option value="">{t("All departments")}</option>
                  {departments.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </Select>
              </Field>
              {can.write && missing.length > 0 && (
                <Button
                  variant="primary"
                  onClick={() =>
                    mark(
                      missing.map((r) => r.employeeId),
                      "present",
                    )
                  }
                  disabled={set.isPending}
                  data-testid="hr-att-all-present"
                >
                  ✓ {t("Mark the {n} not recorded as present", { n: missing.length })}
                </Button>
              )}
            </div>
            <div className={h.stats}>
              {filters.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={h.stat}
                  style={{ ["--tint" as string]: f.c, outline: show === f.key ? `2px solid ${f.c}` : undefined }}
                  onClick={() => setParams(f.key === "all" ? {} : { show: f.key }, { replace: true })}
                >
                  <span className={h.statN}>{f.n}</span>
                  <span className={h.statLabel}>{t(f.label)}</span>
                </button>
              ))}
            </div>
          </Card>
          {q.isLoading && <Skeleton rows={10} />}
          {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
          {q.data &&
            (visible.length === 0 ? (
              <EmptyState>{t("Nobody here.")}</EmptyState>
            ) : (
              <Card
                title={niceDate(day)}
                sub={
                  can.write
                    ? t("Tap a status to record it; it saves at once.")
                    : t("Recording attendance needs the write permission.")
                }
              >
                <Table>
                  <thead>
                    <tr>
                      <th>{t("Person")}</th>
                      <th>{t("Department")}</th>
                      <th>{t("Status")}</th>
                      <th>{t("In")}</th>
                      <th className={s.num}>{t("Minutes late")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => (
                      <tr key={r.employeeId} data-testid="hr-att-row">
                        <td>
                          <Link
                            to={fileLink(r.employeeId, "attendance")}
                            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                          >
                            <Avatar name={r.name} size={28} />
                            {r.name}
                          </Link>
                        </td>
                        <td>{r.department ?? "—"}</td>
                        <td>
                          {can.write ? (
                            <span className={h.statusBtns}>
                              {ATTENDANCE.slice(0, 6).map((a) => (
                                <button
                                  key={a.key}
                                  type="button"
                                  className={h.statusBtn}
                                  style={{ ["--c" as string]: a.colour }}
                                  aria-pressed={r.status === a.key}
                                  title={t(a.label)}
                                  onClick={() => mark([r.employeeId], a.key)}
                                  data-testid={`hr-att-${a.key}`}
                                >
                                  {t(a.label)}
                                </button>
                              ))}
                            </span>
                          ) : r.status ? (
                            t(ATTENDANCE_BY.get(r.status)?.label ?? r.status)
                          ) : (
                            <span className={s.dim}>{t("Not recorded")}</span>
                          )}
                        </td>
                        <td className={s.mono}>{r.checkIn ? r.checkIn.slice(0, 5) : "—"}</td>
                        <td className={s.num}>{r.minutesLate || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>
            ))}
          {set.error && <p className={s.err}>{set.error instanceof Error ? set.error.message : String(set.error)}</p>}
        </>
      )}
    </Page>
  );
}
